import * as React from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import type { AppFile } from './models/JobSharedInterfaces';
import JobTabsData from './JobTabs';
import ConfirmPopup from './ConfirmPopup';
import JobPhotoViewerData from './JobPhotoViewer';
import { DownloadFilesViaPost, GetQuery, PostFormDataQuery, PostQuery } from './Shared';

interface PhotoItem {
    insertedDateFormatted: string;
    photos: AppFile[];
}

const JobPhotosData = () => {
    const [loading, setLoading] = React.useState(true);
    let navigate = useNavigate();
    const { id } = useParams();
    const [siteAddress, setSiteAddress] = React.useState("");
    const [photoList, setPhotoList] = React.useState<PhotoItem[]>([]);
    const [photoLoading, setPhotoLoading] = React.useState(false);
    const [showConfirmModal, setShowConfirm] = React.useState(false);
    const [modalSaveDisabled, setModalSaveDisabled] = React.useState(false);
    const [showPhotoViewer, setShowPhotoViewer] = React.useState(false);
    const [photoIndex, setPhotoIndex] = React.useState({ dateIndex: 0, photoIndex: 0 });

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const apiUrl = 'GetJobPhotos?JobId=' + id;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            var jobPhotos = result.data.jobPhotos;
            for (var i = 0; i < result.data.length; i++) {
                jobPhotos[i].selected = false;
            }
            setPhotoList(jobPhotos);
            setSiteAddress(result.data.siteAddress);
            setLoading(false);
        }
    }

    const handleImportFile = (e: any) => {
        setPhotoLoading(true);
        importPhotos(e.target.files);
    }

    const importPhotos = async (files: File[]) => {
        const filedata = new FormData()
        var jobId = id ?? "";
        filedata.append('jobId', jobId);

        for (var i = 0; i < files.length; i++) {
            filedata.append('files', files[i]);
        }

        const apiUrl = "SaveJobPhotos";
        var result = await PostFormDataQuery(apiUrl, filedata);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            setPhotoLoading(false);
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                getData();
                toast.success("Photos have been uploaded");
            }
        }
    }

    //SELECT PHOTOS
    const selectPhoto = (e: any, dateIndex: number, photoIndex: number) => {
        e.preventDefault();

        var photos = [...photoList];
        var photosItems = photos[dateIndex].photos;
        var photoItem = photosItems[photoIndex];
        photoItem.selected = !photoItem.selected;

        setPhotoList(photos)
    }
    //END SELECT

    //DELETE
    const checkPhotosSelected = () => {
        let photosSelected = false;

        for (var i = 0; i < photoList.length; i++) {
            var photos = photoList[i].photos;
            for (var x = 0; x < photos.length; x++) {
                if (photos[x].selected) {
                    photosSelected = true;
                    break;
                }
            }
        }

        return photosSelected;
    }

    const deletePhoto = (e: any) => {
        e.preventDefault();
        if (checkPhotosSelected()) {
            setShowConfirm(true);
            setModalSaveDisabled(false);
        } else {
            toast.error("Select one or more photos to delete!");
        }
    }

    const hideConfirmModal = () => {
        setShowConfirm(false);
        setModalSaveDisabled(false);
    }

    const saveConfirmModal = () => {
        if (modalSaveDisabled) {
            return;
        }

        deleteJobPhoto();
    }

    const getPhotosSelected = () => {
        let photoIds = [];

        for (var i = 0; i < photoList.length; i++) {
            var photos = photoList[i].photos;
            for (var x = 0; x < photos.length; x++) {
                if (photos[x].selected) {
                    photoIds.push(photos[x].id);
                }
            }
        }

        return photoIds;
    }

    const deleteJobPhoto = async () => {
        var jobPhotos = [...photoList];
        var photoIds = getPhotosSelected();

        var photodata = {
            photoIds: photoIds
        };

        var jsonItem = JSON.stringify(photodata);

        const apiUrl = "DeleteJobPhotos";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                deleteSelectedPhotos(jobPhotos);
                setShowConfirm(false);
                setModalSaveDisabled(true);
                toast.success("Photo/s have been deleted");
            }
        }
    }

    const deleteSelectedPhotos = (jobPhotos: PhotoItem[]) => {
        var jobPhotosLength = jobPhotos.length - 1;

        for (var i = jobPhotosLength; i >= 0; i--) {
            var photos = jobPhotos[i].photos;
            var photosLength = photos.length - 1;
            for (var x = photosLength; x >= 0; x--) {
                if (photos[x].selected) {
                    photos.splice(x, 1);
                }
            }
            if (photos.length === 0) {
                //need to remove the whole photo item 
                jobPhotos.splice(i, 1);
            }
        }

        setPhotoList(jobPhotos);
    } 
    //END DELETE

    //DOWNLOAD PHOTOS
    const downloadPhotos = (e: any) => {
        e.preventDefault();
        if (checkPhotosSelected()) {
            downloadSelectedPhotos();
        } else {
            toast.error("Select one or more photos to download!");
        }
    }

    const downloadSelectedPhotos = async () => {
        var photoIds = getPhotosSelected();
        var fileName = "Job " + id + " Photos.zip";

        var photodata = {
            photoIds: photoIds,
            jobId: id
        };

        var jsonItem = JSON.stringify(photodata);

        const apiUrl = "DownloadJobPhotos";
        await DownloadFilesViaPost(apiUrl, jsonItem, fileName);
    }
    //END DOWNLOAD PHOTOS

    //PHOTO VIEWER
    const openPhotoViewer = (e: any, dateIndex: number, photoIndex: number) => {
        e.preventDefault();
        setPhotoIndex({ dateIndex: dateIndex, photoIndex: photoIndex });
        setShowPhotoViewer(true);
    }

    const hidePhotoViewer = () => {
        setPhotoIndex({ dateIndex: 0, photoIndex:0 });
        setShowPhotoViewer(false);
    }
    //END PHOTO VIEWER

    const tabChanged = (url: string) => {
        navigate(url);
    }

    const confirmPopup = <ConfirmPopup heading="Delete Photo/s?" text="Are you sure you want to delete selected photos?" hideConfirmModal={hideConfirmModal} showConfirmModal={showConfirmModal} saveConfirmModal={saveConfirmModal} />
    const photoViewer = <JobPhotoViewerData photoList={photoList} origDateIndex={photoIndex.dateIndex} origPhotoIndex={photoIndex.photoIndex} showModal={showPhotoViewer} hideModal={hidePhotoViewer} />
    const jobTab = <JobTabsData jobId={Number(id)} siteAddress={siteAddress} tab={"photos"} tabChanged={tabChanged} />;

    const renderDetails = (
        <div>
            <div className="static-modal">
                {confirmPopup}
                {photoViewer}
            </div>
            {jobTab}
            <div className="marginTop10">
                <div className={photoLoading ? "hidden" : "makeitflexspacebetween"}>
                    <input type="file" name="file" accept="image/*" onChange={handleImportFile} multiple />
                    <div>
                        <button className="defaultbutton defaultbutton__small marginRight10" onClick={downloadPhotos}>Download</button>
                        <button className="defaultbutton defaultbutton__small" onClick={deletePhoto}>Delete</button>
                    </div>
                </div>
                <hr></hr>
                <div className={photoList.length === 0 ? "hidden" : "jobphotos--height overflowAuto"}>
                    {photoList.map((photoItem, index) =>
                        <div key={index} className="marginBottom10">
                            <div><h1>{photoItem.insertedDateFormatted}</h1></div>
                            <div className="makeitflexwrap">
                            {photoItem.photos.map((photo, photoIndex) =>
                                <div key={photo.id} className="marginRight20 marginTop10 photoImport__deleteParent--position" >
                                    <a className={"jobphotos--select" + (photo.selected ? " makeitdashgreen" : " makeitgray")} href="#" onClick={(e) => selectPhoto(e, index, photoIndex)}>
                                        <i className="fa-solid fa-circle-check mobileMenuSize alignIconCenter"></i>
                                    </a>

                                    <img className="jobPhotoSize" src={photo.id > 0 ? "data:" + photo.fileType + ";base64," + photo.content : photo.content} onClick={(e) => openPhotoViewer(e, index, photoIndex)}></img>
                                </div>
                            )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobPhotosData;